import React from 'react'
import styled, { css } from 'styled-components'
import { ArrowLeftIcon } from '@mote/assets/icons'
import { Block, Button, useWizard } from '@mote/ui'
import { useOnboarding } from '../../context'

export const WizardController = ({ isMobile, onFinish }) => {
  return (
    <Root>
      <Block
        layout='horizontal'
        align='center'
        justify='space-between'
        minHeight={40}
        gap={16}
        spreadX
      >
        <PreviousButton />
        <StepsIndicator />
        <StepProgress />
        {!isMobile && <NextButton onFinish={onFinish} />}
      </Block>
    </Root>
  )
}

const PreviousButton = ({ onClick, ...rest }) => {
  const { canGoToPrevStep, goToPrevStep } = useWizard()

  function handleOnClickPrev() {
    goToPrevStep()
  }

  if (!canGoToPrevStep) return null

  return (
    <PrevBtn onClick={onClick ?? handleOnClickPrev} {...rest}>
      <ArrowLeftIcon color='currentColor' />
    </PrevBtn>
  )
}

const NextButton = ({ onFinish, ...rest }) => {
  const {
    isMultipleMode,
    isLastStep,
    isNextDisabled,
    canGoToNextStep,
    goToNextStep
  } = useWizard()
  const { isLoading: isStepLoading } = useOnboarding()

  async function handleOnClickNext() {
    if (isLastStep) {
      await onFinish?.()
    }

    goToNextStep()
  }

  if (!(isLastStep || (canGoToNextStep && isMultipleMode))) return null

  return (
    <NextBtn
      text={isLastStep ? 'Finish' : 'Next'}
      disabled={isNextDisabled}
      loading={isStepLoading}
      loadingText='Posting'
      iconPosition='right'
      onClick={handleOnClickNext}
      {...rest}
    />
  )
}

const StepsIndicator = () => {
  const { currentStep, totalSteps } = useWizard()

  return (
    <StepsIndicatorContainer>
      {currentStep + 1} of {totalSteps}
    </StepsIndicatorContainer>
  )
}

const StepProgress = () => {
  const { currentStep, totalSteps } = useWizard()

  return (
    <ProgressContainer>
      <Progress index={currentStep} totalSteps={totalSteps} />
    </ProgressContainer>
  )
}

const Root = styled.div`
  margin: 24px 0;
  transition: oppacity 100ms ease-in;

  ${({ disabled }) =>
    disabled &&
    css`
      opacity: 0.5;
      pointer-events: none;
    `};

  @media (max-width: 639px) {
    margin: 24px 0 0;
  }
`

const NextBtn = styled(Button)`
  min-width: 120px;
`

const PrevBtn = styled.button`
  color: #25252d;
  display: flex;
  flex: 0 0 auto;
  outline: none;
  cursor: pointer;
  justify-content: center;
  align-items: center;
  touch-action: manipulation;
  border-radius: 4px;
  border: none;
  height: 40px;
  width: 24px;
  font-size: 15px;
  line-height: 100%;
  appearance: none;
  user-select: none;

  svg {
    width: 24px;
    height: 24px;
  }

  &:hover {
    color: #9b09d1;
  }

  &:active {
    color: #7807a2;
  }

  &:focus {
    outline: none;
  }
`

const StepsIndicatorContainer = styled.span`
  display: block;
  font-size: 15px;
  line-height: 120%;
  font-weight: 500;
  font-style: normal;
  color: #646f7b;
  white-space: nowrap;
`

const ProgressContainer = styled.div`
  overflow: hidden;
  width: 100%;
  background: #ececf3;
  border-radius: 9px;
`

const Progress = styled.div`
  height: 8px;
  border-radius: 9px;
  background: #ac0ae8;
  transition: width 250ms ease-in;

  width: ${({ index, totalSteps }) =>
    css`calc((${index} * 2 + 1) / ${(totalSteps - 1) * 2} * 100%)`};
`

export const WizardControls = {
  StepsIndicator,
  StepProgress,
  PreviousButton,
  NextButton
}
